import {
    CampaignErrorsData,
    JointCampaignState,
    mappingCampaignErrorsToState,
    mappingCampaignStateToDTO,
} from 'utils/campaign/mappers';
import { CampaignFields, CreateCampaignData, EditCampaignData } from 'utils/campaign/requirementUtils/types';
import { CampaignProcess, CreateCampaignValidatorStep, EditCampaignValidatorStep } from 'utils/constants/campaign';
import convertObjectToPaths, { type ConvertSettings } from 'utils/convertObjectToPaths';

interface FieldForValidation {
    name: keyof Partial<EditCampaignData & CreateCampaignData>;
    needValidate: boolean;
    convertSettings?: ConvertSettings;
}

export const CREATE_CAMPAIGN_FIELDS_FOR_VALIDATION: Record<CreateCampaignValidatorStep, FieldForValidation[]> = {
    [CreateCampaignValidatorStep.First]: [
        { name: CampaignFields.Banner, needValidate: true },
        { name: CampaignFields.CampaignTitle, needValidate: true },
    ],
    [CreateCampaignValidatorStep.Second]: [
        { name: CampaignFields.Areas, needValidate: true, convertSettings: { useParentPath: true } },
        { name: CampaignFields.Banner, needValidate: true },
        { name: CampaignFields.Sex, needValidate: true },
        { name: CampaignFields.AgeMin, needValidate: true },
        { name: CampaignFields.AgeMax, needValidate: true },
        { name: CampaignFields.ProfessionalRoles, needValidate: true },
        { name: CampaignFields.SalaryRange, needValidate: true, convertSettings: { useParentPath: true } },
    ],
    [CreateCampaignValidatorStep.Third]: [
        { name: CampaignFields.Banner, needValidate: false },
        { name: CampaignFields.Sources, needValidate: true },
        { name: CampaignFields.Budget, needValidate: true },
        { name: CampaignFields.BudgetDaily, needValidate: true },
        { name: CampaignFields.Sources, needValidate: false },
        { name: CampaignFields.VacancyId, needValidate: false },
    ],
};

export const EDIT_CAMPAIGN_FIELDS_FOR_VALIDATION: Record<EditCampaignValidatorStep, FieldForValidation[]> = {
    [EditCampaignValidatorStep.First]: [
        { name: CampaignFields.Areas, needValidate: true, convertSettings: { useParentPath: true } },
        { name: CampaignFields.CampaignId, needValidate: false },
        { name: CampaignFields.CampaignTitle, needValidate: true },
        { name: CampaignFields.Sex, needValidate: true },
        { name: CampaignFields.AgeMin, needValidate: true },
        { name: CampaignFields.AgeMax, needValidate: true },
        { name: CampaignFields.ProfessionalRoles, needValidate: true },
        { name: CampaignFields.SalaryRange, needValidate: true, convertSettings: { useParentPath: true } },
    ],
    [EditCampaignValidatorStep.Second]: [
        { name: CampaignFields.Banner, needValidate: false },
        { name: CampaignFields.Sources, needValidate: true },
        { name: CampaignFields.CampaignId, needValidate: false },
        { name: CampaignFields.Budget, needValidate: true },
        { name: CampaignFields.BudgetDaily, needValidate: true },
        { name: CampaignFields.Sources, needValidate: false },
        { name: CampaignFields.VacancyId, needValidate: false },
    ],
};

type Fields = Partial<Record<CampaignFields, unknown>>;

const CREATE_CAMPAIGN_STEPS = [
    CreateCampaignValidatorStep.First,
    CreateCampaignValidatorStep.Second,
    CreateCampaignValidatorStep.Third,
];
const EDIT_CAMPAIGN_STEPS = [EditCampaignValidatorStep.First, EditCampaignValidatorStep.Second];

const getFieldsForStep = (step: number, isEdit: boolean): FieldForValidation[] => {
    if (isEdit) {
        return EDIT_CAMPAIGN_FIELDS_FOR_VALIDATION[EDIT_CAMPAIGN_STEPS[step]];
    }
    return CREATE_CAMPAIGN_FIELDS_FOR_VALIDATION[CREATE_CAMPAIGN_STEPS[step]];
};

interface GetValidateCampaignFieldsData {
    objectToValidate: Record<string, unknown>;
    fieldsToValidate: string[];
}

export const getValidateCampaignFields = (step: number, state: JointCampaignState): GetValidateCampaignFieldsData => {
    const isEdit = state.campaignProcess === CampaignProcess.Edit;

    const fields: FieldForValidation[] = getFieldsForStep(step, isEdit);

    const data = mappingCampaignStateToDTO({ ...state });

    const objectToValidate: Fields = {};
    const fieldsNeedValidate: Fields = {};
    const settingsToValidate = {} as Record<CampaignFields, ConvertSettings>;

    fields.forEach(({ name, needValidate, convertSettings }) => {
        if (data.hasOwnProperty(name)) {
            objectToValidate[name] = data[name];
            if (needValidate) {
                fieldsNeedValidate[name] = data[name];
            }
            if (convertSettings) {
                settingsToValidate[name] = convertSettings;
            }
        }
    });

    const pathsForValidate = convertObjectToPaths(fieldsNeedValidate, settingsToValidate);

    return {
        objectToValidate,
        fieldsToValidate: [...pathsForValidate],
    };
};

type GetValidateErrors = (errors: Record<string, string>) => CampaignErrorsData;

export const getValidateErrors: GetValidateErrors = (errors) => {
    const errorData: CampaignErrorsData = mappingCampaignErrorsToState(errors);

    const result: CampaignErrorsData = Object.fromEntries(Object.entries(errorData).filter(([, value]) => !!value));

    return result;
};
